import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { fabric } from 'fabric';
import { Location, VPT } from '../state/temporary-user-metadata-repository.service';
import { MIN_ZOOM_FOR_TOUCHE_DEVICE } from './sessions-vpt.service';
import { DeviceAndBrowserDetectorService } from './device-and-browser-detector.service';
import { FLAGS, FlagsService } from './flags.service';

@Injectable({
  providedIn: 'root',
})
export class ForcingMinimumZoomService {
  private readonly minTouchDeviceZoom = MIN_ZOOM_FOR_TOUCHE_DEVICE / 100;
  private _currentZoomIsForcedToMinimum$ = new BehaviorSubject<boolean>(false);

  constructor(
    private deviceAndBrowserDetectorService: DeviceAndBrowserDetectorService,
    private flagsService: FlagsService,
  ) {}

  public readonly currentZoomIsForcedToMinimum$ =
    this._currentZoomIsForcedToMinimum$.asObservable();

  public get currentZoomIsForcedToMinimum(): boolean {
    return this._currentZoomIsForcedToMinimum$.value;
  }

  public handleForceMinimumTouchZoom(
    vpt: VPT,
    location: Location,
    followerCanvasSize: [number, number],
  ): VPT {
    if (!this.flagsService.isFlagEnabled(FLAGS.TOUCH_MINIMUM_ZOOM_NOTIFICATIONS)) {
      return vpt;
    }
    const shouldForceMinimumZoom = this.shouldForceMinimumTouchZoom(vpt);
    if (shouldForceMinimumZoom) {
      vpt = this.buildMinTouchZoomVpt(location, followerCanvasSize);
    }
    this._currentZoomIsForcedToMinimum$.next(shouldForceMinimumZoom);
    return vpt;
  }

  private shouldForceMinimumTouchZoom(vpt: VPT): boolean {
    const zoom = this.getZoomFactorFromVpt(vpt);
    return this.deviceAndBrowserDetectorService.isTouchDevice() && zoom < this.minTouchDeviceZoom;
  }

  /**
   * Builds a VPT clamped to minTouchDeviceZoom while keeping the canvas point that is
   * at the center of the leader's screen at the center of the follower's screen.
   */
  private buildMinTouchZoomVpt(
    leaderLocation: Location,
    followerCanvasSize: [number, number],
  ): VPT {
    const leaderScreenCenter = this.getCanvasCenterPoint(leaderLocation.canvasSize);
    const followerScreenCenter = this.getCanvasCenterPoint(followerCanvasSize);
    const canvasPointAtLeaderCenter = fabric.util.transformPoint(
      leaderScreenCenter,
      fabric.util.invertTransform(leaderLocation.vpt),
    );

    return [
      this.minTouchDeviceZoom,
      0,
      0,
      this.minTouchDeviceZoom,
      followerScreenCenter.x - canvasPointAtLeaderCenter.x * this.minTouchDeviceZoom,
      followerScreenCenter.y - canvasPointAtLeaderCenter.y * this.minTouchDeviceZoom,
    ];
  }

  private getCanvasCenterPoint(canvasSize: [number, number]): fabric.Point {
    return new fabric.Point(canvasSize[0] / 2, canvasSize[1] / 2);
  }

  private getZoomFactorFromVpt(vpt: VPT): number {
    return vpt[0];
  }

  // adjust the last vpt for touch devices to ensure the zoom is not below the minimum
  public adjustLastVptForTouchDevices(vpt: VPT): VPT {
    if (
      !this.deviceAndBrowserDetectorService.isTouchDevice() ||
      !this.flagsService.isFlagEnabled(FLAGS.TOUCH_MINIMUM_ZOOM_NOTIFICATIONS)
    ) {
      return vpt;
    }
    const zoom = this.getZoomFactorFromVpt(vpt);
    if (zoom < this.minTouchDeviceZoom) {
      vpt = [this.minTouchDeviceZoom, 0, 0, this.minTouchDeviceZoom, vpt[4], vpt[5]];
    }
    return vpt;
  }

  public cleanUp() {
    this._currentZoomIsForcedToMinimum$.next(false);
  }
}
